import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { excelExport } from 'bricks/utils';
import { resolveUserDisplayName } from 'components/UserDisplay/helpers';
import { getOrganizationUsers } from 'hooks/useGetOrganizationUsers';
import useToast from 'hooks/useToast';
import { QUERY_KEYS } from 'services/urls';
import { trackDatadogRumError } from 'utils/initDatadogRum';
import { buildTasksExportData } from '../excelExport/buildTasksExportData';
import { TASKS_EXPORT_SHEET_NAME } from '../excelExport/constants';
import { buildTasksExportFileName } from '../excelExport/helpers';
import { TimelineComment } from '../types';
import { getTimelines, getTimelinesQueryKey } from './useGetTimeline';
import { getTimelineLatestComments } from './useGetTimelineLatestComments';
import { getTimelineSections, getTimelineSectionsQueryKey } from './useGetTimelineSections';

type UseExportTasksParams = {
    orgId: string;
    dealId: number;
    dealName?: string | null;
};

/**
 * Downloads every task in the deal as an .xlsx. Fetches the data through the query client
 * rather than reading what the table happens to have rendered, so the file always covers the
 * whole deal and is up to date at the moment of export.
 */
export const useExportTasks = ({ orgId, dealId, dealName }: UseExportTasksParams) => {
    const queryClient = useQueryClient();
    const { showError } = useToast();
    const [isExporting, setIsExporting] = useState(false);

    const exportTasks = async () => {
        setIsExporting(true);
        try {
            // Org users are fetched here rather than read off OrganizationUsersContext: the context
            // is still loading on a fresh page load, and an export started then would write a blank
            // Assignees column instead of waiting.
            const [timelines, sections, orgUsers] = await Promise.all([
                queryClient.fetchQuery({
                    queryKey: getTimelinesQueryKey(orgId, dealId, true),
                    queryFn: () => getTimelines({ orgId, dealId, includeAssignees: true }),
                }),
                queryClient.fetchQuery({
                    queryKey: getTimelineSectionsQueryKey(orgId, dealId),
                    queryFn: () => getTimelineSections({ orgId, dealId }),
                }),
                queryClient.fetchQuery({
                    queryKey: [QUERY_KEYS.ORGANIZATION.USERS, orgId],
                    queryFn: () => getOrganizationUsers(orgId),
                    staleTime: Infinity,
                }),
            ]);

            const timelineIds = timelines.map(timeline => Number(timeline.id)).filter(Number.isFinite);
            const latestComments: Record<string, TimelineComment> = timelineIds.length
                ? await queryClient.fetchQuery({
                    queryKey: [QUERY_KEYS.TIMELINE_LATEST_COMMENTS, orgId, dealId, timelineIds],
                    queryFn: () => getTimelineLatestComments(orgId, dealId, timelineIds),
                })
                : {};

            const usersByCorrelationId = new Map(orgUsers.map(orgUser => [orgUser.correlationId, orgUser]));

            const reportData = buildTasksExportData({
                timelines,
                sections,
                latestComments,
                resolveAssigneeName: correlationId => resolveUserDisplayName(usersByCorrelationId.get(correlationId), false),
            });
            await excelExport(buildTasksExportFileName(dealName), true, reportData, false, [], TASKS_EXPORT_SHEET_NAME);
        } catch (error) {
            trackDatadogRumError(
                error instanceof Error ? error : new Error('[useExportTasks] Failed to export tasks'),
                { orgId, dealId },
            );
            showError('Failed to export tasks. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    return { exportTasks, isExporting };
};
